import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Faq from '../components/services/Faq/Faq';
import ServiceBlock from '../components/services/ServiceBlock/ServiceBlock';
import ServicesHero from '../components/services/ServicesHero/ServicesHero';
import ContactSection from '../components/site/ContactSection/ContactSection';
import Section from '../components/ui/Section/Section';
import SectionHeader from '../components/ui/SectionHeader/SectionHeader';
import { CONTACT_PATH, SITE_ORIGIN } from '../data/navigation';
import { services } from '../data/services';
import { servicesPageStructuredData } from '../head/structuredData';
import { usePageMeta } from '../hooks/usePageMeta';
import { useLanguage } from '../i18n/useLanguage';
import type { ServicesPageCopy } from '../locales/types';
import styles from './Services.module.css';

const SERVICES_PATH = '/services';

export default function Services() {
    const { t } = useTranslation();
    const { language, localizedPath } = useLanguage();
    const copy = t('servicesPage', { returnObjects: true }) as unknown as ServicesPageCopy;
    const structuredData = useMemo(() => servicesPageStructuredData({
        language,
        url: `${SITE_ORIGIN}${localizedPath(SERVICES_PATH)}`,
        catalogName: copy.catalogName,
        services: services.map((service) => ({ name: copy.items[service.id].title, description: copy.items[service.id].lead })),
        faq: copy.faq.items
    }), [language, localizedPath, copy]);
    usePageMeta(t('meta.servicesTitle'), t('meta.servicesDescription'), structuredData);
    const contactHref = localizedPath(CONTACT_PATH);

    return (
        <>
            <ServicesHero />

            <Section theme="light" shape="sheet">
                <div className={`page-inner ${styles.blocks}`}>
                    {services.map((service) => (
                        <ServiceBlock key={service.id} service={service} copy={copy.items[service.id]} labels={copy.labels} contactHref={contactHref} />
                    ))}
                </div>
            </Section>

            <Section theme="dark" shape="sheet" labelledBy="services-process-title">
                <div className="page-inner">
                    <SectionHeader id="services-process-title" title={copy.process.title} lead={copy.process.lead} />
                    <ol className={styles.process}>
                        {copy.process.steps.map((step, index) => (
                            <li key={step.title} className={styles.step}>
                                <span className={styles.stepIndex} aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
                                <h3 className={styles.stepTitle}>{step.title}</h3>
                                <p className={styles.stepBody}>{step.body}</p>
                            </li>
                        ))}
                    </ol>
                </div>
            </Section>

            <Section theme="light" shape="sheet" labelledBy="services-reasons-title">
                <div className="page-inner">
                    <SectionHeader id="services-reasons-title" title={copy.reasons.title} />
                    <ul className={styles.reasons}>
                        {copy.reasons.items.map((reason) => (
                            <li key={reason.title} className={styles.reason}>
                                <h3 className={styles.reasonTitle}>{reason.title}</h3>
                                <p>{reason.body}</p>
                            </li>
                        ))}
                    </ul>
                </div>
            </Section>

            <Section theme="dark" shape="sheet" labelledBy="services-faq-title">
                <div className="page-inner">
                    <SectionHeader id="services-faq-title" title={copy.faq.title} lead={copy.faq.lead} />
                    <Faq items={copy.faq.items} />
                </div>
            </Section>

            <ContactSection />
        </>
    );
}
